import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { CheckCircle, ArrowRight, Loader2, Home, XCircle, Shield, CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);

  // Booking state
  const [showBooking, setShowBooking] = useState(false);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [bookingTime, setBookingTime] = useState('');
  const [bookingMessage, setBookingMessage] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingDone, setBookingDone] = useState(false);

  const verifyPayment = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/payments/status/${sessionId}`);
      setPaymentInfo(response.data);
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        if (response.data.payment_type === 'tenant_viewing') {
          setShowBooking(true);
        }
      } else {
        setStatus('pending');
      }
    } catch (error) {
      setStatus('error');
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) verifyPayment();
  }, [sessionId, verifyPayment]);

  const handleBooking = async () => {
    if (!selectedDate || !bookingTime || !paymentInfo?.property_id) return;
    setBookingLoading(true);
    try {
      await axios.post(`${API_URL}/api/bookings`, {
        property_id: paymentInfo.property_id,
        date: selectedDate.toISOString().split('T')[0],
        time: bookingTime,
        message: bookingMessage
      });
      setBookingDone(true);
      setShowBooking(false);
    } catch (error) {
      toast.error('Could not book viewing. You can schedule from the property page.');
      setShowBooking(false);
    } finally {
      setBookingLoading(false);
    }
  };

  const SYMBOLS = { GBP: '£', INR: '₹', CAD: 'C$', USD: '$', EUR: '€' };
  const currencySymbol = SYMBOLS[paymentInfo?.currency?.toUpperCase()] || paymentInfo?.currency?.toUpperCase() || '';

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      
      <div className="max-w-2xl mx-auto px-4 py-20">
        {status === 'loading' && (
          <div className="luxury-card p-16 text-center">
            <Loader2 className="h-16 w-16 text-[#E07A3A] mx-auto animate-spin mb-6" />
            <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-2">Verifying Payment</h2>
            <p className="text-slate-500">Please wait while we confirm your transaction...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-6 animate-fade-in-up">
            {/* Payment Confirmed */}
            <div className="luxury-card p-10 text-center">
              <div className="w-20 h-20 bg-green-50 mx-auto mb-6 flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A] mb-3">Payment Successful</h2>
              <p className="text-slate-500 mb-2">Your payment has been processed successfully.</p>
              
              {paymentInfo && (
                <div className="p-4 bg-[#FEF3EC] my-4 inline-block">
                  <p className="text-[#1A1A1A] font-medium">
                    Amount: {currencySymbol} {(paymentInfo.amount_total / 100).toFixed(2)}
                  </p>
                </div>
              )}

              <div className="my-4 p-4 bg-amber-50 border border-amber-200 text-left">
                <div className="flex items-center gap-2 text-amber-700 mb-1">
                  <Shield className="h-4 w-4" />
                  <span className="font-medium text-sm">Privacy Notice</span>
                </div>
                <p className="text-amber-600 text-sm">
                  For your privacy, contact details require admin approval before being shared. You'll be notified once approved.
                </p>
              </div>
            </div>

            {/* Booking Calendar (for tenant viewing) */}
            {showBooking && !bookingDone && (
              <div className="luxury-card p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-[#FEF3EC] flex items-center justify-center">
                    <CalendarIcon className="h-5 w-5 text-[#E07A3A]" />
                  </div>
                  <div>
                    <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold">Schedule Your Viewing</h3>
                    <p className="text-slate-500 text-sm">Choose your preferred date and time</p>
                  </div>
                </div>

                <div className="space-y-5">
                  <div>
                    <Label className="text-[#1A1A1A]/70 mb-2 block font-semibold text-sm tracking-wider uppercase">Select Date</Label>
                    <div className="flex justify-center bg-[#F8FAFC] p-3 border border-slate-200">
                      <Calendar 
                        mode="single" 
                        selected={selectedDate} 
                        onSelect={setSelectedDate} 
                        disabled={(date) => date < new Date()}
                        data-testid="booking-calendar"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#1A1A1A]/70 font-semibold text-sm tracking-wider uppercase">Preferred Time</Label>
                    <Input 
                      type="time" 
                      value={bookingTime} 
                      onChange={(e) => setBookingTime(e.target.value)} 
                      className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12 mt-1" 
                      data-testid="booking-time-input" 
                    />
                  </div>
                  <div>
                    <Label className="text-[#1A1A1A]/70 font-semibold text-sm tracking-wider uppercase">Message (Optional)</Label>
                    <Textarea 
                      value={bookingMessage} 
                      onChange={(e) => setBookingMessage(e.target.value)} 
                      placeholder="Any special requests or notes..." 
                      className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] mt-1" 
                      data-testid="booking-message-input" 
                    />
                  </div>
                  <div className="p-3 bg-amber-50 border border-amber-200">
                    <p className="text-xs text-amber-700 leading-relaxed">
                      By confirming, you acknowledge Shopperden arranges introductions only. Verify all property details independently and engage qualified professionals before any commitment. Service fees are non-refundable.
                    </p>
                  </div>
                  <Button 
                    onClick={handleBooking} 
                    disabled={bookingLoading || !selectedDate || !bookingTime} 
                    className="w-full bg-[#E07A3A] text-white hover:bg-[#C4652E] h-12 font-semibold tracking-wider"
                    data-testid="confirm-booking-btn"
                  >
                    {bookingLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'CONFIRM VIEWING'}
                  </Button>
                </div>
              </div>
            )}

            {/* Booking Confirmed */}
            {bookingDone && (
              <div className="luxury-card p-10 text-center">
                <div className="w-16 h-16 bg-green-50 mx-auto mb-4 flex items-center justify-center">
                  <CalendarIcon className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-2">Viewing Booked!</h3>
                <p className="text-slate-500">
                  {selectedDate && `${selectedDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}`}
                  {bookingTime && ` at ${bookingTime}`}
                </p>
                <p className="text-sm text-[#1A1A1A]/50 mt-2">Our team will coordinate the visit. Check your dashboard for updates.</p>
              </div>
            )}

            {/* Navigation */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {paymentInfo?.property_id && (
                <Link to={`/property/${paymentInfo.property_id}`}>
                  <Button className="bg-[#E07A3A] text-white hover:bg-[#C4652E] tracking-wider w-full" data-testid="back-property-btn">
                    <ArrowRight className="mr-2 h-4 w-4" />
                    VIEW PROPERTY
                  </Button>
                </Link>
              )}
              <Link to="/buyer">
                <Button variant="outline" className="border-[#E07A3A] text-[#E07A3A] hover:bg-[#FEF3EC] tracking-wider w-full" data-testid="go-dashboard-btn">
                  <Home className="mr-2 h-4 w-4" />
                  MY DASHBOARD
                </Button>
              </Link>
            </div>
          </div>
        )}

        {status === 'pending' && (
          <div className="luxury-card p-16 text-center animate-fade-in-up">
            <Loader2 className="h-16 w-16 text-amber-500 mx-auto mb-6" />
            <h2 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A] mb-3">Payment Processing</h2>
            <p className="text-slate-500 mb-6">Your payment is being processed. This may take a moment.</p>
            <Button onClick={verifyPayment} className="bg-[#E07A3A] text-white hover:bg-[#C4652E]">Check Again</Button>
          </div>
        )}

        {status === 'error' && (
          <div className="luxury-card p-16 text-center animate-fade-in-up">
            <div className="w-20 h-20 bg-red-50 mx-auto mb-6 flex items-center justify-center">
              <XCircle className="h-10 w-10 text-red-600" />
            </div>
            <h2 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A] mb-3">Something Went Wrong</h2>
            <p className="text-slate-500 mb-6">We couldn't verify your payment. Please try again.</p>
            <Link to="/properties">
              <Button className="bg-[#E07A3A] text-white hover:bg-[#C4652E]">Back to Properties</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
